// @ts-check
import React from 'react';
import BaseController from './BaseController';

import FeedbackPopup from '../components/FeedbackPopup';
import SharePopup from '../components/share/SharePopup';
import SharePopupEmbed from '../components/share/SharePopupEmbed';
import DataClassificationPopup from '../components/dataClassification/DataClassificationPopup';
import SaveProjectPopup from '../components/editProject/SaveProjectPopup';
import EditProjectErrorPopup from '../components/editProject/EditProjectErrorPopup';
import SubmitFacilityPopup from '../components/submitFacility/SubmitFacilityPopup';
import LayerLibraryEditor from '../components/layerLibrary/list/LayerLibraryEditor';
import LayerLibraryGroupInfo from '../components/layerLibrary/list/LayerLibraryGroupInfo';
import UserMapViewEditPopup from '../components/mapControls/mapViews/UserMapViewEditPopup';
import ColorPaletteEditor from '../components/colorPaletteEditor/ColorPaletteEditor';
import DeleteUserLayer from '../components/userDataUploadEditor/DeleteUserLayer';
import AddCustomAnalysisValuePopup from '../components/locationPanel/AddCustomAnalysisValuePopup';
import AutoAdjustFailedPopup from '../components/visualizationLegend/AutoAdjustFailedPopup';

import FeedbackDataSource from '../dataSources/FeedbackDataSource';
import UserInfoDataSource from '../dataSources/UserInfoDataSource';
import ProjectDataSource from '../dataSources/ProjectDataSource';
import MailHookDataSource from '../dataSources/MailHookDataSource';

import Errors from '../enums/Error';
import KeyCode from '../enums/KeyCode';
import { hasParentNodeWithClass } from '../helpers/Util';
import UserMapViewPopupMode from '../enums/UserMapViewPopupMode';
import ReportLimitsPopup from '../components/ReportLimitsPopup';

class PopupController extends BaseController {
    static get name() {
        return 'PopupController';
    }

    static getInstance(options) {
        return new PopupController(options);
    }

    onActivate() {
        this.bindGluBusEvents({
            COLOR_PALETTE_POPUP_REQUEST: this.onColorPalettePopupRequest,
            FEEDBACK_POPUP_REQUEST: this.onFeedbackPopupRequest,
            SHARE_POPUP_REQUEST: this.onSharePopupRequest,
            SHARE_EMBED_POPUP_REQUEST: this.onShareEmbedPopupRequest,
            SEND_FEEDBACK_REQUEST: this.onFeedbackSendRequest,
            DATA_CLASSIFICATION_POPUP_REQUEST: this.onDataClassificationPopupRequest,
            SAVE_PROJECT_POPUP_REQUEST: this.onSaveProjectPopupRequest,
            EDIT_PROJECT_POPUP_REQUEST: this.onEditProjectPopupRequest,
            COPY_PROJECT_POPUP_REQUEST: this.onCopyProjectPopupRequest,
            SAVE_PROJECT_ERROR_POPUP_REQUEST: this.onSaveProjectErrorPopupRequest,
            SUBMIT_MISSING_FACILITY_POPUP_REQUEST: this.onSubmitMissingFacilityPopupRequest,
            PICK_MISSING_FACILITY_LOCATION_REQUEST: this.onPickMissingFacilityLocationRequest,
            SUBMIT_EDITED_FACILITY_POPUP_REQUEST: this.onSubmitEditedFacilityPopupRequest,
            DELETE_USER_LAYER_POPUP_REQUEST: this.onDeleteUserLayerPopupRequest,
            ADD_LAYER_LIBRARY_LAYERS_POPUP_REQUEST: this.onAddLayersPopupRequest,
            SHOW_LAYER_LIBRARY_GROUP_DETAILS_POPUP_REQUEST:
                this.onShowLayerLibraryGroupDetailsRequest,
            ADD_USER_MAP_VIEW_POPUP_REQUEST: this.onAddUserMapViewPopupRequest,
            EDIT_USER_MAP_VIEW_POPUP_REQUEST: this.onEditUserMapViewPopupRequest,
            ADD_LOCATION_ANALYSIS_VALUE_POPUP_REQUEST: this.onAddCustomAnalysisValuePopupRequest,
            AUTO_ADJUST_FAILED_POPUP_REQUEST: this.onAutoAdjustFailedPopupRequest,
            SUBMIT_FACILITY_DATA_CHANGE_REQUEST: this.onFacilityDataChangeRequest,
            REPORT_LIMITS_POPUP_REQUEST: this.onReportLimitsPopupRequest,
        });
        this.projectDataSource = this.activateSource(ProjectDataSource);
        this.feedbackDataSource = this.activateSource(FeedbackDataSource);
        this.userInfoDataSource = this.activateSource(UserInfoDataSource);
        this.mailHookDataSource = this.activateSource(MailHookDataSource);
    }

    onAutoAdjustFailedPopupRequest() {
        this.bus.emit('OPEN_MODAL', {
            modalContent: React.createElement(AutoAdjustFailedPopup),
            name: 'Auto adjust failed',
            title: 'modals.autoAdjustFailed',
            modalBoxClass: 'modal-box--auto-adjust-failed',
            modalHeaderClasses: 'modal-header--auto-adjust-failed',
            canClickAway: true,
        });
    }

    onAddCustomAnalysisValuePopupRequest(request) {
        this.bus.emit('OPEN_MODAL', {
            modalContent: React.createElement(AddCustomAnalysisValuePopup, {
                analysisType: request.analysisType,
                existingValues: request.values,
            }),
            name: 'Add new location analysis value',
            title: 'modals.addLocationAnalysisValue',
            modalBoxClass: 'modal-box--add-location-analysis-value',
            canClickAway: true,
        });
    }

    onSubmitMissingFacilityPopupRequest(request) {
        const mapInstance = this.projectDataSource.defaultMapInstance;
        const pin = mapInstance && mapInstance.locationAnalysisItem;
        // a point handed back by the pin round trip wins over the analysis pin
        const pinPoint = (request && request.pinPoint) || (pin && pin.point) || undefined;
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.submitFacility.reportMissing',
            modalBoxClass: 'submit-facility',
            name: 'Report a missing facility',
            canClickAway: false,
            modalContent: React.createElement(SubmitFacilityPopup, {
                type: 'selfstorage_missing_facility_report',
                editMode: false,
                pinPoint,
                initialValues: request && request.initialValues,
                canPickOnMap: this.canPickLocationOnMap,
            }),
        });
    }

    // one map only: the pin request arms every open map viewer, so a split frame
    // has no single map to pin on — same rule the header's map pin follows
    get canPickLocationOnMap() {
        const frame = this.projectDataSource.currentFrame;
        return !!frame && !!frame.mapInstances && frame.mapInstances.length === 1;
    }

    // closes the form, arms the map for a pin drop and reopens the form on the
    // point that comes back, carrying the values the user had already entered
    onPickMissingFacilityLocationRequest({ pinPoint, values }) {
        this._pendingMissingFacility = { pinPoint, values };
        this.bus.emit('CLOSE_MODAL');
        this.bus.once('PICK_MAP_POINT_RESPONSE', this.onMissingFacilityLocationPicked);
        document.addEventListener('keydown', this.onMissingFacilityPickKeyDown);
        // next tick, so the click that opened the pin drop cannot close it again
        this._pickClickTimeout = setTimeout(() => {
            document.addEventListener('click', this.onMissingFacilityPickClick);
        }, 0);
        this.bus.emit('PICK_MAP_POINT_REQUEST');
    }

    onMissingFacilityLocationPicked = point => {
        const { values } = this._pendingMissingFacility || {};
        this.stopMissingFacilityPick();
        this.bus.emit('SUBMIT_MISSING_FACILITY_POPUP_REQUEST', {
            pinPoint: { lng: point.lng, lat: point.lat },
            initialValues: values,
        });
    };

    // ESC abandons the pin drop and returns the form on the point it opened with
    onMissingFacilityPickKeyDown = event => {
        if (event.code !== KeyCode.ESC) return;
        this.cancelMissingFacilityPick();
    };

    // clicking anywhere but the map abandons it too, so the crosshair cannot
    // outlive the form that asked for it
    onMissingFacilityPickClick = event => {
        if (hasParentNodeWithClass(event.target, 'dragonfly-map')) return;
        this.cancelMissingFacilityPick();
    };

    cancelMissingFacilityPick = () => {
        const { pinPoint, values } = this._pendingMissingFacility || {};
        this.stopMissingFacilityPick();
        this.bus.emit('PICK_MAP_POINT_CANCEL');
        this.bus.emit('SUBMIT_MISSING_FACILITY_POPUP_REQUEST', {
            pinPoint,
            initialValues: values,
        });
    };

    stopMissingFacilityPick = () => {
        this._pendingMissingFacility = undefined;
        clearTimeout(this._pickClickTimeout);
        this.bus.off('PICK_MAP_POINT_RESPONSE', this.onMissingFacilityLocationPicked);
        document.removeEventListener('keydown', this.onMissingFacilityPickKeyDown);
        document.removeEventListener('click', this.onMissingFacilityPickClick);
    };

    onSubmitEditedFacilityPopupRequest(payload) {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.submitFacility.suggestEdits',
            modalBoxClass: 'submit-facility',
            name: 'Suggest edit facility',
            canClickAway: false,
            modalContent: React.createElement(SubmitFacilityPopup, {
                ...payload,
                existingFacility: payload,
                type: 'selfstorage_edit_suggestion',
                editMode: true,
            }),
        });
    }

    onSaveProjectPopupRequest() {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.saveMap',
            modalBoxClass: 'modal-box--save-as-project',
            canClickAway: false,
            modalContent: React.createElement(SaveProjectPopup),
        });
    }

    onEditProjectPopupRequest() {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.saveMap',
            modalBoxClass: 'modal-box--save-as-project',
            name: 'Edit map',
            canClickAway: false,
            modalContent: React.createElement(SaveProjectPopup, {
                isEdit: true,
            }),
        });
    }

    onCopyProjectPopupRequest() {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.copyMap',
            modalBoxClass: 'modal-box--save-as-project',
            name: 'Copy map',
            canClickAway: false,
            modalContent: React.createElement(SaveProjectPopup, {
                isCopy: true,
            }),
        });
    }

    onSaveProjectErrorPopupRequest(r) {
        this.bus.emit('OPEN_MODAL', {
            modalBoxClass: 'modal-box--edit-project-error',
            canClickAway: true,
            name: 'Save Project Error',
            modalContent: React.createElement(EditProjectErrorPopup, {
                error: r.error,
            }),
        });
    }

    onShareEmbedPopupRequest(request) {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.share',
            name: 'Share Embed',
            modalBoxClass: 'modal-box--share-embed',
            modalContent: React.createElement(SharePopupEmbed, {
                project: this.projectDataSource.currentProject,
                snapshot: request.snapshot,
            }),
        });
    }

    onDeleteUserLayerPopupRequest(request) {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.deleteUserLayer',
            name: 'Share',
            modalBoxClass: 'modal-box--small',
            modalContent: React.createElement(DeleteUserLayer, {
                userData: request.userData,
            }),
        });
    }

    onSharePopupRequest(request) {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.shareWithOthers',
            name: 'Share',
            modalBoxClass: 'modal-box--share',
            modalContent: React.createElement(SharePopup, {
                showLoader: request.showLoader,
                project: this.projectDataSource.currentProject,
                currentUser: this.userInfoDataSource.currentUser,
                snapshot: request.snapshot,
                hasUnsavedChanges: request.hasUnsavedChanges,
            }),
        });
    }

    onColorPalettePopupRequest(content) {
        /** @type {import('../').ModalParams} */
        const payload = {
            modalHeaderClasses: 'modal-header--no-header',
            modalBoxClass: 'modal-box--palette-editor',
            canClickAway: false,
            name: 'Color Palette',
            modalContent: React.createElement(ColorPaletteEditor, {
                colorPalette: content.colorPalette,
                colorPalettesList: content.colorPalettesList,
                mapInstanceId: content.mapInstanceId,
                editMode: content.editMode,
            }),
        };
        this.bus.emit('OPEN_MODAL', payload);
    }

    onAddLayersPopupRequest(content) {
        /** @type {import('../../../').ModalParams} */
        const payload = {
            modalClasses: 'modal--fullscreen',
            modalBoxClass: 'modal-box--fullscreen-modal',
            canClickAway: false,
            name: 'Layer library',
            modalContent: React.createElement(LayerLibraryEditor, {
                mapInstanceId: content.mapInstanceId,
                mapInstance: content.mapInstance,
            }),
        };
        this.bus.emit('OPEN_MODAL', payload);
    }

    onShowLayerLibraryGroupDetailsRequest(metadata) {
        /** @type {import('../../../').ModalParams} */
        const payload = {
            modalHeaderClasses: 'modal-header',
            modalBoxClass: 'modal-box--library-group-details',
            canClickAway: true,
            titleOverride: metadata.title,
            name: metadata.title,
            modalContent: React.createElement(LayerLibraryGroupInfo, {
                className: 'modal-display',
                metadata,
            }),
        };
        this.bus.emit('OPEN_MODAL', payload);
    }

    onAddUserMapViewPopupRequest(payload) {
        const mapInstance = this.projectDataSource.getActiveMapInstance(payload.mapInstanceId);
        const currentMapView = mapInstance.initialView;

        /** @type {import('../../../').ModalParams} */
        const popupPayload = {
            modalBoxClass: 'modal-box--user-map-view',
            canClickAway: true,
            title: 'modals.newMapPosition',
            name: 'New map position',
            customFocusElement: true,
            modalContent: React.createElement(UserMapViewEditPopup, {
                mapInstanceId: payload.mapInstanceId,
                title: 'New map position',
                latitude: parseFloat(currentMapView.centerLat).toFixed(5),
                longitude: parseFloat(currentMapView.centerLng).toFixed(5),
                zoom: parseFloat(currentMapView.zoom).toFixed(1),
                mode: UserMapViewPopupMode.CREATE,
            }),
        };
        this.bus.emit('OPEN_MODAL', popupPayload);
    }

    onEditUserMapViewPopupRequest(payload) {
        const {
            item: { id, title, mapView },
            mapInstanceId,
        } = payload;

        /** @type {import('../../../').ModalParams} */
        const popupPayload = {
            modalBoxClass: 'modal-box--user-map-view',
            canClickAway: true,
            title: 'modals.editMapPosition',
            name: 'Edit map position',
            customFocusElement: true,
            modalContent: React.createElement(UserMapViewEditPopup, {
                mapViewId: id,
                mapInstanceId,
                title,
                latitude: parseFloat(mapView.centerLat).toFixed(5),
                longitude: parseFloat(mapView.centerLng).toFixed(5),
                zoom: parseFloat(mapView.zoom).toFixed(1),
                mode: UserMapViewPopupMode.EDIT,
            }),
        };
        this.bus.emit('OPEN_MODAL', popupPayload);
    }

    /**
     * Event handler for SelfStorage location data update
     * @param payload
     *        information about the user and feedback text
     */
    onFacilityDataChangeRequest(payload) {
        const onError = error => {
            this.bus.emit('SUBMIT_FACILITY_DATA_CHANGE_ERROR', {
                level: Errors.CRITICAL,
                originalError: error,
                additionalInfo:
                    'Sending facility data change failed when communicating with API. Check paths.',
            });
        };

        this.mailHookDataSource
            .postSelfStorageFacilityDataChange(payload)
            .then(() => {
                this.bus.emit('UPDATE_MODAL_STATE', {
                    title: '',
                    modalBoxClass: 'submit-facility success',
                    name: 'Facility data submitted successfully',
                });
                this.bus.emit('SUBMIT_FACILITY_DATA_CHANGE_SUCCESS');
            })
            .catch(onError);
    }

    /**
     * Event handler for Feedback Send Request
     * @param payload
     *        information about the user and feedback text
     */
    onFeedbackSendRequest(payload) {
        const onError = error => {
            this.bus.emit('FEEDBACK_ERROR', {
                level: Errors.CRITICAL,
                originalError: error,
                additionalInfo: 'Sending feedback failed when communicating with API. Check paths.',
            });
        };

        this.feedbackDataSource
            .postFeedback(payload)
            .then(() => {
                this.bus.emit('SEND_FEEDBACK_SUCCESS');
                this.bus.emit('CLOSE_MODAL');
            })
            .catch(onError);
    }

    /**
     *  Event handler for popup request
     *  This opens a popup with feedback fields.
     */

    onFeedbackPopupRequest() {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.feedback',
            name: 'Feedback',
            modalBoxClass: 'modal-box--feedback modal-box--medium',
            modalContent: React.createElement(FeedbackPopup),
        });
    }

    /**
     *  Event handler for popup request
     *  This opens a popup with data classification component... pass mapInstanceId to component
     *  @param {String} mapInstanceId
     */
    onDataClassificationPopupRequest(mapInstanceId) {
        this.bus.emit('OPEN_MODAL', {
            title: 'modals.editCutpoints',
            name: 'Data Classification',
            modalHeaderClasses: 'modal-header--no-header',
            canCloseModal: true,
            canClickAway: false,
            modalBoxClass: 'modal-box--edit-cutpoints',
            modalContent: React.createElement(DataClassificationPopup, {
                mapInstanceId,
            }),
        });
    }

    onReportLimitsPopupRequest() {
        this.bus.emit('OPEN_MODAL', {
            title: 'dataBrowser.dataExportsRemaining',
            name: 'Data exports',
            modalBoxClass: 'modal-box--report-limits',
            modalContent: React.createElement(ReportLimitsPopup),
        });
    }

    onDeactivate() {
        this.unbindGluBusEvents();
    }
}

export default PopupController;
